import type { JSONSchema } from "./types.js";

type SchemaPropertyType = "string" | "number" | "integer" | "boolean" | "object" | "array";

interface SchemaPropertyDef {
  type: SchemaPropertyType;
  description?: string;
  optional?: boolean;
  [keyword: string]: unknown;
}

type SchemaDefinition = Record<string, SchemaPropertyDef>;

type InferType<T extends SchemaPropertyType> = T extends "string"
  ? string
  : T extends "number" | "integer"
    ? number
    : T extends "boolean"
      ? boolean
      : T extends "object"
        ? Record<string, unknown>
        : T extends "array"
          ? unknown[]
          : never;

type InferSchema<T extends SchemaDefinition> = {
  [K in keyof T as T[K]["optional"] extends true ? never : K]: InferType<
    T[K]["type"]
  >;
} & {
  [K in keyof T as T[K]["optional"] extends true ? K : never]?: InferType<
    T[K]["type"]
  >;
};

export interface TypedSchema<T> extends JSONSchema {
  __type?: T;
}

export function defineSchema<T extends SchemaDefinition>(
  definition: T
): TypedSchema<InferSchema<T>> {
  const properties: NonNullable<JSONSchema["properties"]> = {};
  const required: string[] = [];

  for (const [key, prop] of Object.entries(definition)) {
    const jsonSchemaProperty: Record<string, unknown> = {};
    for (const [propertyKey, propertyValue] of Object.entries(prop)) {
      if (propertyKey !== "optional") {
        jsonSchemaProperty[propertyKey] = propertyValue;
      }
    }
    Object.defineProperty(properties, key, {
      enumerable: true,
      configurable: true,
      writable: true,
      value: jsonSchemaProperty,
    });
    if (!prop.optional) {
      required.push(key);
    }
  }

  return {
    type: "object",
    properties,
    required,
  };
}
